<script setup lang="ts">
import DoNotContactChip from '@/components/Common/DoNotContactChip.vue';
import DoNotContactPopover from '@/components/Conversations/DoNotContact/DoNotContactPopover.vue';
import { useMeStore } from '@/core/shared/me/me.store';
import dayjs from 'dayjs';
import { computed } from 'vue';

import { type ConversationIndex } from '@/core/conversations/conversation-index/conversation-index.type';
import { ApplicationDisplayUtil } from '@/utils/application-display.util';

const props = defineProps<{
  conversationIndex: ConversationIndex;
  selectable?: boolean;
  selected?: boolean;
}>();

defineEmits<{
  (e: 'toggle-select'): void;
  (e: 'do-not-contact-removed', employerId: number, userProfileId: number): void;
}>();
const meStore = useMeStore();

const parsedConversationIndex = computed(() => {
  /**
   * Get only the first 3 applications and then the rest show them as +{count} more
   */
  let applicationIndexes = props.conversationIndex.applicationIndexes ?? [];
  const totalApplicationCount = applicationIndexes.length;
  applicationIndexes = applicationIndexes.slice(0, 3);

  // '+{count} more' calculation
  const additionalApplicationCount = totalApplicationCount - applicationIndexes.length;
  let additionalApplicationCountString = '';
  if (additionalApplicationCount > 0) {
    additionalApplicationCountString = `+${additionalApplicationCount} more`;
  }

  /**
   * If lastTs:
   * - Between last two weeks - green color | month day || if between 1 day => x hours ago
   * - Longer than two weeks - grey color | month day
   * - Before current year  - grey color | year month day
   */
  const date = dayjs(props.conversationIndex.lastMessageSentTs);
  const now = dayjs();

  const isWithinOneDay = now.diff(date, 'hour') <= 24;
  const isWithinTwoWeeks = now.diff(date, 'day') <= 14;
  const isWithinCurrentYear = now.year() === date.year();
  let timeAgoString = '';
  let timeAgoBgColor = '#DEFAEA';
  let timeAgoTextColor = '#1B7A45';

  if (isWithinCurrentYear) {
    if (isWithinTwoWeeks) {
      if (isWithinOneDay) {
        // Within one day
        timeAgoString = `${now.diff(date, 'hour')} hours ago`;
      } else {
        // Between last two weeks
        timeAgoString = date.format('MMMM DD');
      }
    } else {
      // Longer than two weeks
      timeAgoString = date.format('MMMM DD');
      timeAgoBgColor = '#E2E8F0';
      timeAgoTextColor = '#64748B';
    }
  } else {
    // Before current year
    timeAgoString = date.format('YYYY MMMM DD');
    timeAgoBgColor = '#E2E8F0';
    timeAgoTextColor = '#64748B';
  }

  // Add application formatting
  const applicationDisplay = ApplicationDisplayUtil.formatApplications(props.conversationIndex);

  const data = {
    proName: props.conversationIndex.proName ?? '',
    proEmail: props.conversationIndex.proEmail ?? '',
    proPhoneNumber: props.conversationIndex.proPhoneNumber ?? '',
    proId: props.conversationIndex.proId ?? '',
    lastMessageText: props.conversationIndex.lastMessageText ?? '',
    lastProJobTitle: props.conversationIndex.lastProJobTitle ?? '',
    lastProEmployerName: props.conversationIndex.lastProEmployerName ?? '',
    additionalApplicationCountString,
    lastMessageSentTs: timeAgoString,
    timeAgoBgColor,
    timeAgoTextColor,
    primaryApplication: applicationDisplay.primaryApplication,
    additionalApplicationCount: applicationDisplay.additionalCount,
  };

  return data;
});
</script>

<template>
  <div class="flex items-start gap-3 px-4 py-4">
    <input
      v-if="selectable"
      type="checkbox"
      class="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-highlight-600"
      :checked="selected"
      data-test="candidate-select"
      @click.stop
      @change="$emit('toggle-select')"
    />
    <!--
      Mobile (<md): stack the four columns vertically as a card.
      Desktop (md+): original 10-col table grid, unchanged.
    -->
    <div class="flex min-w-0 flex-1 flex-col gap-1.5 md:grid md:grid-cols-10 md:gap-8">
      <!-- Pro Info -->
      <div class="col-span-3 font-sans text-sm font-normal leading-[18px] text-shade-800">
        <p class="sensitive mb-1.5 truncate font-bold leading-[21px] text-shade-900">
          {{ parsedConversationIndex.proName }}
        </p>
        <DoNotContactPopover
          v-if="conversationIndex.doNotContact === true && meStore.employer?.id"
          :employer-id="meStore.employer.id"
          :user-profile-id="conversationIndex.proId"
          :candidate-name="parsedConversationIndex.proName"
          source="Candidate Archive"
          class="mb-1.5"
          @removed="
            (employerId, userProfileId) =>
              $emit('do-not-contact-removed', employerId, userProfileId)
          "
        />
        <DoNotContactChip v-else-if="conversationIndex.doNotContact === true" class="mb-1.5" />
        <p class="sensitive mb-1.5 truncate">{{ parsedConversationIndex.proEmail }}</p>
        <p class="sensitive mb-1.5 truncate">{{ parsedConversationIndex.proPhoneNumber }}</p>
      </div>

      <!-- Recent Work Experience -->
      <div class="col-span-2 font-sans text-sm font-normal leading-[18px] text-shade-800">
        <p class="mb-1.5 truncate">{{ parsedConversationIndex.lastProJobTitle }}</p>
        <p class="truncate">{{ parsedConversationIndex.lastProEmployerName }}</p>
      </div>

      <!-- Applications -->
      <div class="col-span-3 font-sans text-sm leading-[18px]">
        <p class="mb-1.5 truncate font-bold text-shade-880">
          {{ parsedConversationIndex.primaryApplication }}
        </p>
        <p
          v-if="parsedConversationIndex.additionalApplicationCount"
          class="truncate font-normal text-shade-700"
        >
          {{ parsedConversationIndex.additionalApplicationCount }}
        </p>
      </div>

      <!-- Last Active -->
      <div class="col-span-2 text-sm font-bold leading-[21px] md:text-right">
        <span
          class="box-border inline-block h-[29px] rounded-[20px] px-2 py-1"
          :style="{
            backgroundColor: parsedConversationIndex.timeAgoBgColor,
            color: parsedConversationIndex.timeAgoTextColor,
          }"
        >
          {{ parsedConversationIndex.lastMessageSentTs }}
        </span>
      </div>
    </div>
  </div>
</template>
